package dev.lingvo.site.components.studio.compose

import dev.lingvo.shared.screenshots.ScreenshotEngine
import dev.lingvo.shared.screenshots.doc.DeviceLayer
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.doc.TypeRole
import dev.lingvo.shared.screenshots.render.RenderInputs
import dev.lingvo.shared.screenshots.render.RenderMode
import dev.lingvo.shared.screenshots.render.RenderReport
import dev.lingvo.shared.screenshots.render.effectiveLayers
import dev.lingvo.shared.screenshots.render.primaryCaptionLayer
import dev.lingvo.shared.screenshots.text.DefaultFontResolver
import dev.lingvo.shared.screenshots.text.Scripts
import dev.lingvo.shared.screenshots.text.FontCatalog
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.components.studio.canvas.BitmapImage
import dev.lingvo.site.components.studio.canvas.CanvasMeasurer
import dev.lingvo.site.components.studio.canvas.CanvasSurface
import dev.lingvo.site.components.studio.fonts.FontGate
import dev.lingvo.site.components.studio.fonts.FontLoadRequest
import dev.lingvo.site.components.studio.fonts.FontLoadProgress
import dev.lingvo.site.components.studio.fonts.IntlSegmenter
import dev.lingvo.site.i18n.t

/** Production export adapter. Indices refer to the board's full plan, including neighboring pages.
 * [loadImage] returns a newly owned bitmap, never a shared cache entry; null preserves missing-input reports.
 */
class EngineComposeRenderPort(
    private val design: ScreenshotDesign,
    private val caption: (ComposeJob, TextContent, Int) -> String?,
    private val loadImage: suspend (ComposeJob, ImageSource, Int) -> dynamic,
    private val templateFamilies: Map<TypeRole, String> = emptyMap(),
) : ComposeRenderPort {
    private val engine = ScreenshotEngine.renderer()
    override val ownedBitmaps: Int get() = images.size
    private val images = mutableMapOf<Pair<ImageSource, Int>, BitmapImage>()

    private fun board(job: ComposeJob) = requireNotNull(design.boards.find { it.id == job.boardId })
    private fun page(job: ComposeJob) = requireNotNull(board(job).pages.find { job.slotKey in it.slotIds })
    private fun index(job: ComposeJob, local: Int, offset: Int = 0): Int {
        val board = board(job)
        val start = board.pages.takeWhile { it.id != page(job).id }.sumOf { it.slotIds.size }
        val size = board.pages.sumOf { it.slotIds.size }
        return ((start + local + offset) % size + size) % size
    }
    private fun layers(job: ComposeJob): List<Layer> {
        fun flatten(layer: Layer): List<Layer> = if (layer.base.hidden || layer.base.opacity == 0.0) emptyList() else listOf(layer) +
            if (layer is GroupLayer) layer.children.flatMap { flatten(it) } else emptyList()
        return effectiveLayers(page(job), board(job), job.locale, design.schemaVersion).flatMap { flatten(it) }
    }
    private fun text(job: ComposeJob, layer: TextLayer): String? = when (val content = layer.content) {
        is TextContent.Literal -> content.text
        else -> caption(job, content, index(job, if (content is TextContent.SlotCaption) content.slot else layer.base.roleSlot))
    }

    override suspend fun ensureFonts(jobs: List<ComposeJob>): FontPreflightResult = ensureFonts(jobs) {}

    override suspend fun ensureFonts(jobs: List<ComposeJob>, progress: (FontLoadProgress) -> Unit): FontPreflightResult {
        val failed = FontGate.load(fontRequests(jobs), progress)
        return if (failed == null) FontPreflightResult(true) else FontPreflightResult(false,
            trf("screenshots.compose.family_font_failed", "{family} didn't finish downloading for {language}. No screenshots were composed. Retry.",
                "family" to failed.family, "language" to dev.lingvo.site.components.localeLabel(failed.locale, "")))
    }

    private fun fontRequests(jobs: List<ComposeJob>): List<FontLoadRequest> = buildList {
        for (job in jobs) for (layer in layers(job).filterIsInstance<TextLayer>()) {
            val sample = text(job, layer) ?: continue
            val style = layer.style
            val script = ScreenshotEngine.scripts.scriptFor(job.locale, sample)
            val stack = DefaultFontResolver.stack(style.role, style.family, design.brand, templateFamilies[style.role],
                script, Scripts.hasEmoji(sample), style.weight, style.italic)
            for (family in stack.families) {
                // Brand and Latin fallback faces may have no native glyphs in an all-Arabic caption.
                val coveredSample = if (script in FontCatalog.byName(family)?.scripts.orEmpty() || family == "Noto Color Emoji") sample else "Lingvo"
                add(FontLoadRequest(family, style.weight, style.italic, coveredSample, job.locale))
            }
        }
    }.distinct()

    /** The same effective caption layer supplies the server fit floor. */
    fun floorPx(job: ComposeJob): Double {
        val primary = if (design.schemaVersion >= 3) primaryCaptionLayer(page(job),
            effectiveLayers(page(job), board(job), job.locale, design.schemaVersion), page(job).slotIds.indexOf(job.slotKey))
            else layers(job).filterIsInstance<TextLayer>().firstOrNull { it.content is TextContent.SlotCaption }
        return primary?.fit?.floorSize ?: 0.0
    }

    override suspend fun prepare(job: ComposeJob) {
        closeJob()
        try {
            for (layer in layers(job)) {
                val source = when (layer) { is ImageLayer -> layer.source; is DeviceLayer -> layer.screen; else -> continue }
                val slot = if (source is ImageSource.Capture) index(job, source.slot, source.planOffset)
                    else index(job, layer.base.roleSlot)
                val key = source to slot
                // The runner owns the current capture; every other visible input is scoped to this job.
                if (source is ImageSource.Capture && slot == index(job, page(job).slotIds.indexOf(job.slotKey))) continue
                if (key !in images) {
                    val bitmap = loadImage(job, source, slot)
                    if (bitmap != null) {
                        val image = BitmapImage("$source:$slot", bitmap)
                        val pixels = images.values.sumOf { it.width.toLong() * it.height } + image.width.toLong() * image.height
                        if (pixels > job.width.toLong() * job.height * 2) {
                            image.close()
                            throw IllegalStateException(tr("studio.compose.engine_compose_render_port.this_layout_needs_too_much_image", "This layout needs too much image memory. Use fewer full-size images and retry."))
                        }
                        images[key] = image
                    }
                }
            }
        } catch (failure: Throwable) { closeJob(); throw failure }
    }

    override fun render(job: ComposeJob, capture: dynamic, target: dynamic): RenderReport {
        val context = target.getContext("2d")
        context.fillStyle = design.palette.bg
        context.fillRect(0, 0, job.width, job.height)
        val current = index(job, page(job).slotIds.indexOf(job.slotKey))
        val primary = if (job.captureId != null && capture != null) BitmapImage(job.captureId, capture) else null
        val inputs = RenderInputs(job.locale, RenderMode.EXPORT,
            { content, slot -> caption(job, content, slot) },
            { source, slot -> if (source is ImageSource.Capture && slot == current) primary else images[source to slot] },
            CanvasMeasurer(context), IntlSegmenter(), ScreenshotEngine.fonts, ScreenshotEngine.scripts, templateFamilies)
        return engine.renderSlot(design, requireNotNull(job.boardId), job.slotKey, inputs, CanvasSurface(context, job.locale))
    }

    override fun closeJob() {
        images.values.forEach { it.close() }
        images.clear()
    }
}
